import {
  forwardRef,
  PropsWithoutRef,
  ForwardedRef,
  useState,
  useEffect,
  ComponentType,
} from 'react';
import debounce from 'lodash/debounce';

import noop from 'utils/noop';

import { MultiFilterSelect } from '../FilterSelect';
import { RadioOption } from './RadioOption';
import { RadioExpandableCategoryGroup } from './RadioExpandableCategoryGroup';
import { NestedRadioTrigger } from './NestedRadioTrigger';

import {
  SelectValueType,
  BaseOption,
  ExpandableCategoryProps,
  SelectValueDisplayProps,
  SelectProps,
  MultiSelectProps,
  BasicOptionProps,
} from '../types';

const sortOptions = (opts: BaseOption<string>[]) => {
  return opts.sort((a, b) => {
    const { categoryId: aCat = '', label: aLab = '' } = a;
    const { categoryId: bCat = '', label: bLab = '' } = b;

    // if categoryId arent the same sort on category id
    if (aCat !== bCat) {
      return aCat > bCat ? 1 : -1;
    }

    // else sort on label
    // its possible for the label to be null so check for that
    if (!aLab) {
      return 1;
    } else if (!bLab) {
      return -1;
    }
    return aLab > bLab ? 1 : -1;
  });
};

export const checkCategorySearch = (
  category: { id: string; doubleLine?: string },
  label: string,
  searchString: string,
) => {
  let flag = false;
  // checks all the strings in the object to look for matches to the search string
  Object.values({ ...category, label }).forEach((str) => {
    if (str.toLowerCase().includes(searchString?.toLowerCase())) {
      flag = true;
    }
  });
  return flag;
};

export type NestedRadioSelectProps<TValue extends SelectValueType = string> = SelectProps<TValue> &
  Omit<MultiSelectProps, 'categoryLabels'> & {
    /** a mapping of human readable strings to category id (eg 'transactional channel': '101') */
    categoryLabels: { [key: string]: { id: string; doubleLine?: string; label?: string } };
    /** Text to display next to the count numbers  */
    countText?: { plural: string; single: string };
    /** optional text */
    inputContent?: {
      filterPlaceholder: string;
    };
    isFullWidth?: boolean;
  };

export const NestedRadioSelect = ({
  categoryLabels = {},
  options = [],
  countText = { plural: '', single: '' },
  defaultValues = [],
  onSelect = noop,
  Option = RadioOption as ComponentType<React.PropsWithChildren<BasicOptionProps<string>>>,
  ...props
}: NestedRadioSelectProps<string>) => {
  const [searchString, setSearchString] = useState('');
  const [newCategoryLabels, setNewCategoryLabels] = useState({});

  useEffect(() => {
    const newVals: { [key: string]: string } = {};
    // Parses the category labels to what multifilter select needs eg {[label]:label}
    Object.keys(categoryLabels).forEach((label: string) => {
      if (
        searchString.length < 1 ||
        checkCategorySearch(categoryLabels[label], label, searchString)
      ) {
        newVals[label] = label;
      }
    });
    setNewCategoryLabels(newVals);
  }, [searchString, categoryLabels]);

  // Sorts the options
  const sortedOptions = options;
  sortOptions(sortedOptions);

  // handles the select converting the multi to a single select
  const handleSelect = (val: string[]) => {
    if (val.length > 1) {
      // filter out the previous selection so only most recent selection remains
      const newVals = val.filter((v) => !defaultValues.includes(v));
      onSelect(newVals, { selectedOptions: [] });
    } else if (val.length !== 0 && val[0] !== defaultValues[0]) {
      // if only a single thing is selected and its not the same as the previous set value
      onSelect(val, { selectedOptions: [] });
    }
  };

  const categoryIds: { [key: string]: string } = {};
  Object.keys(categoryLabels).forEach((label) => {
    categoryIds[label] = categoryLabels[label].id;
  });

  // Passes in the info to our custom trigger
  // This needs the category label information to display their name when selected
  const Trigger = forwardRef(
    (
      triggerProps: PropsWithoutRef<SelectValueDisplayProps>,
      ref: ForwardedRef<HTMLButtonElement>,
    ) => <NestedRadioTrigger categoryIds={categoryIds} ref={ref} {...triggerProps} />,
  );

  // Passes in the information to our Radio expandable category
  const CategoryLabel = (categoryProps: ExpandableCategoryProps) => {
    const categoryInfo =
      typeof categoryProps.label === 'string' ? categoryLabels[categoryProps.label] : { id: '' };
    const { id: categoryId } = categoryInfo;

    return (
      <RadioExpandableCategoryGroup
        countText={countText}
        {...categoryProps}
        // override the default isSelected and onClick
        isSelected={defaultValues[0] === categoryId}
        onClick={() => {
          onSelect([categoryId], { selectedOptions: [] });
        }}
        categoryInfo={categoryInfo}
      />
    );
  };

  const handleSearchChange = (search?: string) => {
    setSearchString(search || '');
  };

  return (
    <MultiFilterSelect
      categoryLabels={newCategoryLabels}
      options={sortedOptions}
      isExpandable
      Option={Option}
      ExpandableCategoryGroup={CategoryLabel}
      onSelect={handleSelect}
      onSearchChange={debounce(handleSearchChange, 100)}
      defaultValues={defaultValues}
      showAllCategories={true}
      Trigger={Trigger}
      {...props}
    />
  );
};
